// eslint-disable-next-line import/no-extraneous-dependencies
import { EventEmitter, ListenerFn } from 'eventemitter3';
import _debounce from 'lodash/debounce';
import logger from 'src/logger';
import {
  editButtonType,
  makeEditButton,
  makeTagEditObjectComponent,
  tagEditObjectType,
} from 'src/sdk-components';
import { ComponentInteractionType } from 'src/sdk-components/SceneComponent';
import ThreeInstance from 'src/sdk-components/ThreeInstance';
import { MpSdk } from 'src/types';
import { DeviceManager } from 'src/utils';
import { hexToRgb } from 'src/utils/all';
import { Vector3 } from 'three';
import { DEFAULT_TAG_COLOR } from './constants';
import { TagEventsType, TagType } from './enums';

class Tag {
  loading: boolean;

  matterTag: MpSdk.Mattertag;

  pose: MpSdk.Camera.Pose;

  allTags: Map<string, any>; // TODO: fix type

  editButton?: [any, any];

  pointerNormal: Vector3;

  translateHandlerRef: any;

  on: (type: TagEventsType, callback: ListenerFn) => void;

  emit: (type: TagEventsType, data?: unknown) => void;

  off: (type: TagEventsType, callback?: ListenerFn) => void;

  private mpSDK: MpSdk;

  private appAws: string;

  private appDomain: string;

  private registeredIcons: string[];

  constructor(mpSDK: MpSdk, appAws: string, appDomain: string) {
    this.mpSDK = mpSDK;
    this.matterTag = mpSDK.Mattertag;
    this.allTags = new Map();
    this.appAws = appAws;
    this.appDomain = appDomain;
    this.registeredIcons = [];

    this.add = this.add.bind(this);
    this.edit = this.edit.bind(this);
    this.editTagMedia = this.editTagMedia.bind(this);

    this.mpSDK.Camera.pose.subscribe((p) => (this.pose = p));

    const handleEvent = (event: any) => (tagId: string, eventData?: any) => {
      const tags = Array.from(this.allTags.values());

      const foundTag: any = tags.find((t: any) => t.sid === tagId);
      if (!foundTag) return;

      // hover event has hover: boolean parameter
      this.emit(TagEventsType.OpenTagIframe, {
        event,
        tagId: foundTag.id,
        eventData,
      });
    };

    this.mpSDK.on(this.matterTag.Event.CLICK, handleEvent(this.matterTag.Event.CLICK));
    this.mpSDK.on(this.matterTag.Event.HOVER, handleEvent(this.matterTag.Event.HOVER));

    const interaction = new EventEmitter();

    this.on = function (type: any, callback: ListenerFn) {
      interaction.on(type, callback);
    };

    this.emit = function (type: TagEventsType, data?: unknown) {
      interaction.emit(type, data);
    };

    this.off = function (type: TagEventsType, callback?: ListenerFn) {
      interaction.off(type, callback);
    };
  }

  // TODO:: Add model from other package
  async add(tag: any) {
    if (!tag) {
      return;
    }
    // await this.setIconImage(tag);

    const rgb = hexToRgb(tag.iconColor || tag.color, true);

    const details: MpSdk.Mattertag.MattertagDescriptor = {
      label: tag.title,
      description: tag.description,
      anchorPosition: {
        x: tag.positionX || 0,
        y: tag.positionY || 0,
        z: tag.positionZ || 0,
      },
      stemVector: {
        x: tag.stemVectorX,
        y: tag.stemVectorY,
        z: tag.stemVectorZ,
      },
      color: rgb,
      stemVisible: typeof tag.stemVisible === 'boolean' ? tag.stemVisible : true,
    };
    if (tag.media) {
      details.media = {
        src: tag.media.src,
        type: `mattertag.media.${tag.media.type.toLowerCase()}` as MpSdk.Mattertag.MediaType,
      };
    }
    const [sid] = await this.matterTag.add(details);
    return sid;
  }

  async pointerIntersection(tagNode: any, attachToCursor: any) {
    const THREE = await ThreeInstance.get();
    const handleModelIntersection = (intersectionData: any) => {
      const cursor = new THREE.Vector3(
        intersectionData.position.x,
        intersectionData.position.y,
        intersectionData.position.z,
      );
      const normalizedCursor = new THREE.Vector3(
        intersectionData.normal.x,
        intersectionData.normal.y,
        intersectionData.normal.z,
      ).round();

      if (attachToCursor.current && tagNode.current) {
        // X
        if (normalizedCursor.x === 1 || normalizedCursor.x === -1) {
          tagNode.current.obj3D.position.set(cursor.x, cursor.y, cursor.z);
        }

        // Y
        if (normalizedCursor.y === 1 || normalizedCursor.y === -1) {
          tagNode.current.obj3D.position.set(cursor.x, cursor.y, cursor.z);
        }

        // Z
        if (normalizedCursor.z === 1 || normalizedCursor.z === -1) {
          tagNode.current.obj3D.position.set(cursor.x, cursor.y, cursor.z);
        }
      }
    };

    this.mpSDK.Pointer.intersection.subscribe(handleModelIntersection);
  }

  editMattertag = async (tag: any) => {
    try {
      const iconId = this.getTagIconId(tag);
      if (iconId) {
        await this.matterTag.editIcon(tag.sid, iconId).catch((e) => {
          logger.error(`editMattertag -> editIcon(): `, e);
        });
      } else {
        await this.matterTag.resetIcon(tag.sid).catch((e) => {
          logger.error(`editMattertag -> resetIcon(): `, e);
        });
      }

      if (tag.iconColor && /#\w*/i.test(tag.iconColor)) {
        const rgb = hexToRgb(tag.iconColor, true);
        await this.matterTag.editColor(tag.sid, rgb).catch(logger.error);
      }
      await this.editTagMedia(tag);
    } catch (err) {
      logger.error(err);
    }
  };

  async edit(tag: any) {
    if (!tag) {
      return;
    }
    this.setIconImage(tag);

    if (tag.opacity !== null && typeof tag.opacity === 'number') {
      await this.matterTag.editOpacity(tag.sid, tag.opacity / 100).catch(logger.error);
    }

    if (tag.title) {
      await this.matterTag.editBillboard(tag.sid, {
        label: tag.title,
        description: tag.description?.replace(/<[^>]*>?/gm, ''),
      });
    }

    if (typeof tag.stemVisible === 'boolean') {
      await this.matterTag.editStem(tag.sid, {
        stemVisible: tag.stemVisible,
      });
    }

    if (!tag.mpTitle) {
      await this.matterTag.editBillboard(tag.sid, {
        label: '',
        description: tag.description?.replace(/<[^>]*>?/gm, ''),
      });
    }

    if (tag.iconColor && /#\w*/i.test(tag.iconColor) && !tag.iconUrl) {
      const rgb = hexToRgb(tag.iconColor, true);
      await this.matterTag.editColor(tag.sid, rgb);
    }

    this.matterTag
      .editPosition(tag.sid, {
        anchorPosition: {
          x: tag.positionX || 0,
          y: tag.positionY || 0,
          z: tag.positionZ || 0,
        },
        stemVector: {
          x: typeof tag.stemVectorX === 'number' ? tag.stemVectorX : 0,
          y: typeof tag.stemVectorY === 'number' ? tag.stemVectorY : 0.3,
          z: typeof tag.stemVectorZ === 'number' ? tag.stemVectorZ : 0,
        },
        floorIndex: tag.floor,
      })
      .catch(logger.error);

    await this.matterTag.preventAction(tag.sid, {
      opening: tag.treedisStyle || !!tag?.iotDevices?.length,
      navigating: !tag.mpNavigation,
    });

    this.allTags.set(tag.sid, tag);
    return tag.sid;
  }

  async editTagMedia(tag: any) {
    if (tag.opacity !== null && typeof tag.opacity === 'number') {
      await this.matterTag.editOpacity(tag.sid, tag.opacity / 100);
    }

    const label = tag.mpTitle ? tag.title : '';
    const description = tag.description;

    if (tag.embedMedia && tag.enabled) {
      let media;
      const size = DeviceManager.isMobile()
        ? { w: tag.widthMobile || 300, h: tag.heightMobile || 400 }
        : { w: tag.width || 700, h: tag.height || 500 };
      // if (tag.type === TagType.Image && tag.url) {
      //   media = {
      //     type: this.matterTag.MediaType.PHOTO,
      //     src: `${this.appAws}/tours/${tag.tourId}/tags/${tag.url}`
      //   };
      // }

      // if (tag.type === TagType.Video && tag.url) {
      //   media = { type: this.matterTag.MediaType.VIDEO, src: tag.url };
      // }

      if (tag.type === TagType.Iframe) {
        this.matterTag.injectHTML(
          tag.sid,
          `<iframe src="${tag.url}" style="width: 100%; height: 100vh"></iframe>`,
          { size },
        );
      }

      // if (tag.type === TagType.Text) {
      //   this.matterTag.injectHTML(tag.sid, `<div>${tag.text}</div>`, { size });
      // }
      await this.matterTag
        .editBillboard(tag.sid, { label, description, media })
        .catch(logger.error);
      await this.matterTag
        .preventAction(tag.sid, { opening: !tag.enableTooltip, navigating: !tag.mpNavigation })
        .catch(logger.error);
    } else {
      await this.matterTag
        .editBillboard(tag.sid, { label, description: description?.replace(/<[^>]*>?/gm, '') })
        .catch(logger.error);
    }
  }

  async changeTagIconAndColorPUBLIC(tagSid: any, icon: any, color: any) {
    const iconId = `${icon}__${Math.random()}`;
    const iconUrl = `${
      process.env.NEXT_PUBLIC_API
    }/v1/public/icons/${icon}?hex=${encodeURIComponent(color)}`;

    await this.matterTag.registerIcon(iconId, iconUrl).catch((e) => {
      logger.error(e);
    });
    await this.matterTag.editIcon(tagSid, iconId).catch((e) => {
      logger.error(e);
    });
  }

  async toggleVisible({
    tags,
    hiddenTags,
    visibleTags,
    isVisible,
    audioHandler,
  }: {
    tags: any;
    audioHandler?: (src: string) => void;
    hiddenTags: any[];
    visibleTags: any[];
    isVisible: boolean;
  }) {
    tags.forEach(async (_tag: any) => {
      const { sid, enableTooltip, mpNavigation } = _tag;

      if (isVisible && visibleTags[sid]) return;

      visibleTags[sid] = isVisible;

      if (!isVisible && hiddenTags[sid]) return;

      hiddenTags[sid] = isVisible;

      if ((_tag.type === TagType.Music && !isVisible, audioHandler)) {
        const src = `${this.appAws}/tours/${_tag.tourId}/tags/${_tag.url}`;
        audioHandler(src);
        // if (tagAudio && tagAudio.src === src) {
        //   tagAudio.pause();
        //   tagAudio.src = '';
        // }
      }
      await this.matterTag.editOpacity(sid, isVisible ? _tag.opacity / 100 : 0).catch(logger.error);

      await this.matterTag
        .editPosition(sid, {
          anchorPosition: {
            x: _tag.positionX,
            y: _tag.positionY,
            z: _tag.positionZ,
          },
          stemVector: {
            x: isVisible ? _tag.stemVectorX : 0,
            y: isVisible ? _tag.stemVectorY : 0,
            z: isVisible ? _tag.stemVectorZ : 0,
          },
          floorIndex: _tag.floor,
        })
        .catch(logger.error);

      const preventOpening = isVisible ? !enableTooltip : true;
      const preventNavigating = isVisible ? !mpNavigation : true;
      await this.matterTag
        .preventAction(sid, {
          opening: preventOpening,
          navigating: preventNavigating,
        })
        .catch(logger.error);
    });
  }

  private registerIcon = async ({ iconId, url }) => {
    if (!iconId || !url) return;
    await this.matterTag.registerIcon(iconId, url).catch((e) => {
      console.error(e);
    });

    this.registeredIcons = [...this.registeredIcons, iconId];
  };

  // private registerIcons = async (_tags: Array<any>) => {
  //   const tagsWithIcons = _tags.map(this.mapTagWithIcon);

  //   const registerIconsPromises = tagsWithIcons.map((t) =>
  //     this.registerIcon({ iconId: t.iconId, url: t.iconUrl })
  //   );

  //   await Promise.all(registerIconsPromises);

  //   const editIconsPromises = tagsWithIcons.map((t) => {
  //     if (t.iconId && this.registeredIcons.includes(t.iconId)) {
  //       return this.matterTag.editIcon(t.sid, t.iconId);
  //     }
  //     return Promise.resolve();
  //   });

  //   await Promise.all(editIconsPromises).catch((e) => console.error(e));

  //   return tagsWithIcons;
  // };

  private mapTagWithIcon = (tag) => {
    const clone = { ...tag };
    const iconId = this.getTagIconId(tag);

    if (iconId) {
      let tagIconUrl: string | undefined = undefined;

      if (tag.iconFile) {
        if (tag.iconFile.startsWith('http')) {
          tagIconUrl = `${tag.iconFile}`;
        } else {
          tagIconUrl = `${this.appAws}/tours/${tag.tourId}/tags/icons/${tag.iconFile}`;
        }
      } else if (tag.customIcon) {
        tagIconUrl = tag.iconColor
          ? `${this.appDomain}/v1/public/icons/${tag.customIcon}?hex=${encodeURIComponent(
              tag.iconColor,
            )}&circle=${tag.whiteCircle}`
          : `${this.appDomain}/v1/public/icons/${tag.customIcon}`;
      }

      clone.iconUrl = tagIconUrl;
      clone.iconId = iconId;
    }

    return clone;
  };

  public addDummyObject(tag: any, context: any) {
    const THREE = context.three;
    const geometry = new THREE.SphereGeometry(0.1, 32, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffff00,
      depthTest: false,
      depthWrite: false,
    });
    const helperSphere = new THREE.Mesh(geometry, material);
    helperSphere.position.set(
      tag.positionX! + tag.stemVectorX!,
      tag.positionY! + tag.stemVectorY!,
      tag.positionZ! + tag.stemVectorZ!,
    );
    helperSphere.visible = false;
    context.scene.add(helperSphere);
    const dummyObject = helperSphere;
    return { ...tag, dummyObject };
  }

  public removeDummyObject(tag: any, context: any) {
    const dummyObject = tag.dummyObject;
    if (!dummyObject) return;
    dummyObject.geometry.dispose();
    dummyObject.material.dispose();
    context.scene.remove(dummyObject);
  }

  private processTag = async (tag: any) => {
    try {
      const addedCustomTag = Array.from(this.allTags.values()).find(
        (item: { id: number; added: boolean }) => item.id === tag.id && item.added,
      );

      if (!tag.custom || addedCustomTag) {
        await this.edit(tag);
        return;
      }

      if (tag.custom) {
        tag.stemVectorX = tag.stemVectorX || tag.stemVectorX === 0 ? tag.stemVectorX : 0;
        tag.stemVectorY = tag.stemVectorY || tag.stemVectorY === 0 ? tag.stemVectorY : 0.3;
        tag.stemVectorZ = tag.stemVectorZ || tag.stemVectorZ === 0 ? tag.stemVectorZ : 0;

        tag.defaultSid = tag.defaultSid || tag.sid;
        if (!addedCustomTag) {
          tag.sid = await this.add(tag);
          await this.editTagMedia(tag);
          await this.setIconImage(tag);
          tag.added = true;
        }
      }

      const t = await this.mapTagWithIcon(tag);

      await this.registerIcon({ iconId: t.iconId, url: t.iconUrl });

      await this.matterTag.preventAction(tag.sid, {
        opening: tag.treedisStyle || !!tag?.iotDevices?.length,
      });

      if (t.iconId && this.registeredIcons.includes(t.iconId)) {
        // return
        await this.matterTag.editIcon(t.sid, t.iconId);
      }
      this.allTags.set(tag.sid, tag);
    } catch (e) {
      console.error(e);
    }
  };

  async initTags(_tags: Array<any>) {
    if (!_tags.length) {
      return this.allTags;
    }

    // const tagsWithIcons = await this.registerIcons(_tags);

    // const processTagsPromises = tagsWithIcons.map(this.processTag);
    const processTagsPromises = _tags.map(this.processTag);
    await Promise.all(processTagsPromises);

    return this.allTags;
  }

  async moveToTagIO(tag: any, doOpenTag = true) {
    setTimeout(async () => {
      const tags = Array.from(this.allTags.values());
      const foundTag: any = tags.find((t: any) => t.sid === tag.sid);
      if (!foundTag) return;

      if (this.pose?.mode !== this.mpSDK.Mode.Mode.OUTSIDE) {
        await this.matterTag.navigateToTag(foundTag.sid, this.matterTag.Transition.FLY);
      }

      if (doOpenTag)
        this.emit(TagEventsType.OpenTagIframe, {
          tagId: tag.id,
          data: tag,
        });
    }, 100);
  }

  async moveToTag(
    tag: any,
    { onHover, onClick }: { onHover?: (value: any) => void; onClick?: () => void } = {},
  ) {
    const tags = Array.from(this.allTags.values());
    const foundTag: any = tags.find((t: any) => t.sid === tag.sid);

    if (!foundTag) return;

    if (this.pose?.mode !== this.mpSDK.Mode.Mode.OUTSIDE) {
      await this.matterTag.navigateToTag(foundTag.sid, this.matterTag.Transition.INSTANT);
    }
    //!this.editButton?.length
    // Hidden for now. Might be needed in the future
    if (false) {
      const [modelNode, btnComponent] = await this.createEditButton(tag, { onHover, onClick });

      modelNode.start();
      this.editButton = [modelNode, btnComponent];
    }
  }

  async configureScene(controls: any, handleMode?: any) {
    const THREE = await ThreeInstance.get();
    this.pointerNormal = new THREE.Vector3(0, 1, 0);
    await this.mpSDK.Scene.configure((renderer: any, three: any) => {
      renderer.physicallyCorrectLights = true;
      renderer.gammaFactor = 2.2;
      renderer.gammaOutput = true;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.bias = 0.0001;
      renderer.shadowMap.type = three.PCFSoftShadowMap;
    });

    await this.mpSDK.Scene.register(tagEditObjectType, makeTagEditObjectComponent);
    await this.mpSDK.Scene.register(editButtonType, makeEditButton);

    this.mpSDK.Pointer.intersection.subscribe((e) => {
      const { x, y, z } = e.normal;
      this.pointerNormal.set(x, y, z);
    });

    // Create a scene node with a transform control component.
    const node = await this.mpSDK.Scene.createNode();
    controls.current = node.addComponent('mp.transformControls');
    node.start();
    node.visible = false;

    if (handleMode) {
      this.mpSDK.Camera.pose.subscribe(handleMode);
    }
  }

  async createEditButton(
    tag: any,
    { onHover, onClick }: { onHover?: (value: any) => void; onClick?: () => void },
  ) {
    try {
      const modelNode: any = await this.mpSDK.Scene.createNode();
      const btnSize = 0.02;
      const btnComponent = modelNode.addComponent(editButtonType, {
        size: btnSize,
        src: 'https://cdn.treedis.com/dev/users/1/logo.png',
        localPosition: {
          x: tag.stemVectorX - tag.stemVectorX * 0.25,
          y: tag.stemVectorY + tag.stemVectorY * 0.15,
          z: 0,
        },
      });
      btnComponent.onEvent = (type: ComponentInteractionType, eventData: any) => {
        if (type === ComponentInteractionType.HOVER && onHover) {
          onHover(eventData?.hover);
        }

        if (type === ComponentInteractionType.CLICK && onClick) {
          onClick();
        }
      };

      modelNode.obj3D.position.set(tag.positionX, tag.positionY, tag.positionZ);
      const { _x: x, _y: y, _z: z, _w: w } = modelNode.targets.camera.quaternion;
      modelNode.obj3D.quaternion.set(x, y, z, w);

      return [modelNode, btnComponent];
    } catch (e) {
      console.error(e);
    }
    return [];
  }

  async removeEditButton() {
    if (this.editButton?.length) {
      this.editButton[0].stop();
      this.editButton = undefined;
    }
  }

  async remove(sid: string) {
    const removedTag = await this.matterTag
      .remove(sid)
      .then(() => this.allTags.delete(sid))
      .catch((err: any) => {
        logger.error(`Tag "${sid}" was not removed. Message: ${err.message || err}`);
      });

    return removedTag;
  }

  async toggleVisibleEditButton() {
    if (this.editButton?.length) {
      this.editButton[0].obj3D.visible = !this.editButton[0].obj3D.visible;
    }
  }

  async initTagEditObject({
    controls,
    tagNode,
    inputNodeComponent,
    options = {},
    userNavigationEnabled,
    tag,
  }: {
    controls: any;
    tagNode: any;
    inputNodeComponent: any;
    options?: any;
    userNavigationEnabled?: boolean;
    tag?: any;
  }) {
    if (controls.current) {
      // creating the oriented box scene node
      tagNode.current = await this.mpSDK.Scene.createNode();
      tagNode.current.addComponent(tagEditObjectType, {
        ...options,
        normal: this.pointerNormal,
        pose: this.pose,
        tag,
      });
      if (!userNavigationEnabled) {
        inputNodeComponent.current = tagNode.current.addComponent('mp.input', {
          eventsEnabled: true,
          userNavigationEnabled: false,
          unfiltered: false,
        });
      }

      // the position will be updated regarding the user cursor position
      tagNode.current.obj3D.position.set(0, 0, 0);
      tagNode.current.start();
    }
  }

  async startTranslateControls({
    tag,
    tagNode,
    inputNodeComponent,
    controls,
    cb,
  }: {
    tag: any;
    tagNode: any;
    inputNodeComponent: any;
    controls: any;
    cb: () => void;
  }) {
    // const { currentFloor } = await this.mpSDK.Floor.getData();
    // // move tag far from user camera
    // this.mpSDK.Mattertag.editPosition(tag.sid, {
    //   anchorPosition: {
    //     x: 0,
    //     y: 100,
    //     z: 0
    //   },
    //   floorIndex: currentFloor
    // });
    const THREE = await ThreeInstance.get();
    await this.initTagEditObject({
      controls,
      tagNode,
      inputNodeComponent,
      options: {
        stemVector: new THREE.Vector3(tag.stemVectorX, tag.stemVectorY, tag.stemVectorZ),
      },
      userNavigationEnabled: true,
      tag,
    });

    tagNode.current.obj3D.position.set(tag.positionX, tag.positionY, tag.positionZ);

    controls.current.outputs.objectRoot.visible = true;
    controls.current.inputs.selection = tagNode.current;
    controls.current.inputs.mode = 'translate';

    this.translateHandlerRef = _debounce(() => {
      if (controls.current.inputs.selection) {
        cb();
      }
    }, 200);

    controls.current.outputs.objectRoot.addEventListener('change', this.translateHandlerRef);
  }

  stopPositionUpdates({ controls }) {
    controls.current.outputs.objectRoot.removeEventListener('change', this.translateHandlerRef);
  }

  private getTagIconId = (tag) => {
    const iconName =
      tag.iconFile ||
      tag.iconUrl?.split('/icons/')[1] ||
      (tag.customIcon === 'null' || tag.customIcon === null ? null : tag.customIcon);
    const iconSid = tag.sid;

    if (iconName && iconSid) {
      return `${iconName}_${iconSid}`;
    }
    return null;
  };

  private setIconImage = async (tag: any) => {
    try {
      if (tag.iconUrl) {
        const iconId = this.getTagIconId(tag);
        if (!iconId) return;
        // If it has already been registered, only edit it
        if (this.registeredIcons.includes(iconId)) {
          await this.matterTag.editIcon(tag.sid, iconId);
        } else {
          await this.matterTag.registerIcon(iconId, tag.iconUrl).then(() => {
            this.registeredIcons = [...this.registeredIcons, iconId];

            this.matterTag.editIcon(tag.sid, iconId);
          });
        }
      } else {
        if (!tag.customIcon && !tag.iconFile) {
          await this.matterTag.resetIcon(tag.sid);
        }
      }
    } catch (e) {
      logger.error(`setIconImage(): `, e);
    }
  };
}

export { Tag, DEFAULT_TAG_COLOR, TagType, TagEventsType };
